/**
 * Contenu des explicatifs de rôle (page d'accueil).
 *
 * Chaque rôle expose des « scènes » ordonnées, jouées comme une petite histoire
 * animée : une illustration de fond (sans texte incrusté) + un sous-titre lu à
 * voix haute et surligné mot par mot (karaoké).
 *
 * Principe clé : `text` sert À LA FOIS de sous-titre affiché ET de texte narré,
 * pour que le surlignage karaoké corresponde exactement à la voix.
 *
 * Rôles (4) :
 *   - parrain_grand  : Parrain Grand Électeur (mobilisateur/influence, PRD § 4.0)
 *   - relais_prive   : Relais Privé (invité par un Parrain)
 *   - relais_public  : Relais Public (visible sur la plateforme, communauté)
 *   - beneficiaire   : Bénéficiaire (citoyen à inscrire)
 *
 * Forme d'une scène :
 *   {
 *     id: string,
 *     text: string,           // affiché (karaoké) ET narré
 *     keyword?: string,       // mot mis en avant (or)
 *     image?: string,         // illustration de fond (public/), sans texte incrusté
 *     motion?: string,        // variante d'animation de la couche décorative
 *     durationMs: number,     // repli minuté si la voix est indisponible
 *     isCta?: boolean,        // dernière scène : afficher l'appel à l'action
 *     audioSrc?: string|null, // fichier audio enregistré (ElevenLabs / futur wolof)
 *     timingsSrc?: string|null, // timings mot-à-mot (précision karaoké, optionnel)
 *   }
 *
 * Les `audioSrc`/`timingsSrc` sont renseignés APRÈS génération audio
 * (voir scripts/onboarding/generate-onboarding-audio.mjs). Tant qu'ils sont nuls,
 * la narration utilise la synthèse vocale de l'appareil, puis un repli minuté.
 */

import { applyNativeWoAudio } from './native-wo.segments.js';
import { applyNativeWoTranscripts } from './native-wo.transcripts.js';

export const ROLE_KEYS = ['parrain_grand', 'relais_prive', 'relais_public', 'beneficiaire'];

/**
 * Langues proposées dans le lecteur. Une langue n'est activable pour un rôle que si
 * son bloc `languages[code]` existe (voir isLanguageAvailable). Le wolof (`wo`) est
 * prévu : ajoutez les scènes wolof + générez l'audio (`--lang wo`) pour l'activer.
 */
/** Langue ouverte par défaut dans le lecteur (audio wolof natif). */
export const DEFAULT_EXPLAINER_LANG = 'wo';

/** Drapeau = pays associé à la langue parlée (repère visuel, pas une restriction géo). */
export const LANGUAGES = [
  { code: 'wo', label: 'Wolof', short: 'WO', flag: '🇸🇳' },
  { code: 'fr', label: 'Français', short: 'FR', flag: '🇫🇷' },
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
];

/** Titres de rôle affichés dans l'en-tête du lecteur, par langue. */
export const ROLE_LABELS = {
  parrain_grand: {
    fr: 'Parrain Grand Électeur',
    wo: 'Parrain Grand Électeur',
    en: 'Grand Elector Sponsor',
  },
  relais_prive: {
    fr: 'Relais Privé',
    wo: 'Relais Privé',
    en: 'Private Relay',
  },
  relais_public: {
    fr: 'Relais Public',
    wo: 'Relais Public',
    en: 'Public Relay',
  },
  beneficiaire: {
    fr: 'Bénéficiaire',
    wo: 'Bénéficiaire',
    en: 'Beneficiary',
  },
};

/** Libellés du lecteur (boutons, aria) — FR pour wo/fr, EN pour la diaspora anglophone. */
export const EXPLAINER_MODAL_UI = {
  wo: {
    chooseLang: 'Choisir la langue',
    langSoon: (label) => `${label} — bientôt disponible`,
    close: 'Fermer l’explication',
    sceneProgress: (cur, tot) => `Scène ${cur} sur ${tot}`,
    goScene: (n) => `Aller à la scène ${n}`,
    voiceFallback: 'Voix indisponible sur cet appareil : les sous‑titres défilent automatiquement.',
    register: 'S’inscrire',
    login: 'Se connecter',
    replay: '↺ Revoir l’explication',
    controls: 'Commandes de lecture',
    prev: 'Scène précédente',
    play: 'Lire',
    pause: 'Mettre en pause',
    next: 'Scène suivante',
    unmute: 'Activer le son',
    mute: 'Couper le son',
  },
  fr: {
    chooseLang: 'Choisir la langue',
    langSoon: (label) => `${label} — bientôt disponible`,
    close: 'Fermer l’explication',
    sceneProgress: (cur, tot) => `Scène ${cur} sur ${tot}`,
    goScene: (n) => `Aller à la scène ${n}`,
    voiceFallback: 'Voix indisponible sur cet appareil : les sous‑titres défilent automatiquement.',
    register: 'S’inscrire',
    login: 'Se connecter',
    replay: '↺ Revoir l’explication',
    controls: 'Commandes de lecture',
    prev: 'Scène précédente',
    play: 'Lire',
    pause: 'Mettre en pause',
    next: 'Scène suivante',
    unmute: 'Activer le son',
    mute: 'Couper le son',
  },
  en: {
    chooseLang: 'Choose language',
    langSoon: (label) => `${label} — coming soon`,
    close: 'Close',
    sceneProgress: (cur, tot) => `Scene ${cur} of ${tot}`,
    goScene: (n) => `Go to scene ${n}`,
    voiceFallback: 'Voice unavailable on this device: subtitles scroll automatically.',
    register: 'Sign up',
    login: 'Log in',
    replay: '↺ Watch again',
    controls: 'Playback controls',
    prev: 'Previous scene',
    play: 'Play',
    pause: 'Pause',
    next: 'Next scene',
    unmute: 'Unmute',
    mute: 'Mute',
  },
};

/** Musique de fond (bed) jouée en boucle, à faible volume, sous la narration. */
export const BG_MUSIC_SRC = '/onboarding/audio/bg/ambient.mp3';

const IMG = '/onboarding/img';

/** Fusionne textes + visuels FR ; audio natif (wo) ou ElevenLabs appliqué dans getRoleContent. */
function sceneLang(frVisuals, textScenes, { langCode, audio = true, nativeAudio = false }) {
  return {
    langCode,
    audio,
    nativeAudio,
    scenes: textScenes.map((s, i) => ({
      id: s.id,
      text: s.text,
      keyword: s.keyword,
      image: frVisuals[i]?.image,
      motion: frVisuals[i]?.motion,
      durationMs: s.durationMs,
      isCta: s.isCta ?? frVisuals[i]?.isCta,
      audioSrc: null,
      timingsSrc: null,
    })),
  };
}

function woLang(frVisuals, woScenes) {
  return sceneLang(frVisuals, woScenes, { langCode: 'wo', audio: true, nativeAudio: true });
}

function enLang(frVisuals, enScenes) {
  return sceneLang(frVisuals, enScenes, { langCode: 'en-US', audio: true, nativeAudio: false });
}

const CONTENT = {
  parrain_grand: {
    label: 'Parrain Grand Électeur',
    icon: '👑',
    registerRole: 'parrain_grand',
    languages: {
      fr: {
        langCode: 'fr-FR',
        audio: true,
        scenes: [
          {
            id: 'welcome',
            text: 'Bienvenue ! Découvrez le rôle du Parrain Grand Électeur.',
            keyword: 'Parrain',
            image: `${IMG}/parrain-welcome.webp`,
            motion: 'welcome',
            durationMs: 4200,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'influence',
            text: 'Votre force, c’est votre influence : au Sénégal comme dans la diaspora, votre parole mobilise votre entourage.',
            keyword: 'influence',
            image: `${IMG}/parrain-influence.webp`,
            motion: 'network',
            durationMs: 7000,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'campaigns',
            text: 'Vous lancez des campagnes de massification et vous désignez des Relais Privés de confiance pour enrôler les bénéficiaires sur le terrain.',
            keyword: 'massification',
            image: `${IMG}/parrain-campaigns.webp`,
            motion: 'campaign',
            durationMs: 7600,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'finance',
            text: 'Votre soutien financier n’est qu’un appui, à la demande de vos Relais : c’est votre engagement qui fait la différence.',
            keyword: 'appui',
            image: `${IMG}/parrain-finance.webp`,
            motion: 'unlock',
            durationMs: 7200,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'cta',
            text: 'Prêt à mettre votre réseau au service de la citoyenneté ? Rejoignez Grand-Électeur.',
            keyword: 'réseau',
            image: `${IMG}/parrain-cta.webp`,
            motion: 'cta',
            durationMs: 5200,
            isCta: true,
            audioSrc: null,
            timingsSrc: null,
          },
        ],
      },
      wo: woLang(
        [
          { image: `${IMG}/parrain-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/parrain-influence.webp`, motion: 'network' },
          { image: `${IMG}/parrain-campaigns.webp`, motion: 'campaign' },
          { image: `${IMG}/parrain-finance.webp`, motion: 'unlock' },
          { image: `${IMG}/parrain-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Sougnou Parrain Grand Électeur. Dalal ak djàm. Ndakh khamga loumiy djérrign!', keyword: 'Parrain', durationMs: 4000 },
          { id: 'influence', text: 'Nga Nekk Senegaal walla bitim-réew, Sa dolé, mooy sa influence. Sa baat day dajalé ñi la wër. Mène nga motivé nit gni!', keyword: 'influence', durationMs: 6000 },
          { id: 'campaigns', text: 'Dangay tàmbali campagne yu mag ngir bind ñu bare, te dangay tànn Relais Privé yu nga wóolu ngir ñu jàppale bénéficiaire yi ci terrain bi.', keyword: 'massification', durationMs: 11000 },
          { id: 'finance', text: 'Bu ko sa Relais yi laadj, moungna len dimballi financierement pour gnou djappallé bénéficiaire yi.', keyword: 'dimballi', durationMs: 8000 },
          { id: 'cta', text: 'Ndax noo waajal a jëfandikoo sa réseau ngir citoyenneté bi ? Bokkal ci Grand-Électeur.', keyword: 'réseau', durationMs: 7000, isCta: true },
        ],
      ),
      en: enLang(
        [
          { image: `${IMG}/parrain-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/parrain-influence.webp`, motion: 'network' },
          { image: `${IMG}/parrain-campaigns.webp`, motion: 'campaign' },
          { image: `${IMG}/parrain-finance.webp`, motion: 'unlock' },
          { image: `${IMG}/parrain-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Welcome! Discover the role of a Grand Elector Sponsor.', keyword: 'Sponsor', durationMs: 4200 },
          { id: 'influence', text: 'Your strength is your influence: in Senegal and across the diaspora, your voice mobilizes your network.', keyword: 'influence', durationMs: 7000 },
          { id: 'campaigns', text: 'You launch mass registration campaigns and appoint trusted Private Relays to enroll beneficiaries in the field.', keyword: 'campaigns', durationMs: 7600 },
          { id: 'finance', text: 'Financial support is only a backup when your Relays ask—it is your commitment that makes the difference.', keyword: 'commitment', durationMs: 7200 },
          { id: 'cta', text: 'Ready to put your network at the service of citizenship? Join Grand-Électeur.', keyword: 'network', durationMs: 5200, isCta: true },
        ],
      ),
    },
  },

  relais_prive: {
    label: 'Relais Privé',
    icon: '🤝',
    registerRole: 'relais',
    languages: {
      fr: {
        langCode: 'fr-FR',
        audio: true,
        scenes: [
          {
            id: 'welcome',
            text: 'Bienvenue ! Découvrez le rôle du Relais Privé.',
            keyword: 'Privé',
            image: `${IMG}/relais_prive-welcome.webp`,
            motion: 'welcome',
            durationMs: 4000,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'invited',
            text: 'Vous êtes un agent de terrain bénévole, invité par un Parrain Grand Électeur qui vous fait confiance.',
            keyword: 'invité',
            image: `${IMG}/relais_prive-invited.webp`,
            motion: 'field',
            durationMs: 6400,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'engine',
            text: 'Vous êtes le moteur de sa campagne de massification : c’est vous qui allez à la rencontre des citoyens.',
            keyword: 'moteur',
            image: `${IMG}/relais_prive-engine.webp`,
            motion: 'campaign',
            durationMs: 6800,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'guide',
            text: 'L’application vous guide pas à pas — du diagnostic au parcours des documents — pour mener chaque bénéficiaire jusqu’au statut « Inscrit ».',
            keyword: 'Inscrit',
            image: `${IMG}/relais_prive-guide.webp`,
            motion: 'parcours',
            durationMs: 8200,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'cta',
            text: 'Prêt à faire inscrire votre communauté ? Rejoignez Grand-Électeur.',
            keyword: 'communauté',
            image: `${IMG}/relais_prive-cta.webp`,
            motion: 'cta',
            durationMs: 4400,
            isCta: true,
            audioSrc: null,
            timingsSrc: null,
          },
        ],
      },
      wo: woLang(
        [
          { image: `${IMG}/relais_prive-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/relais_prive-invited.webp`, motion: 'field' },
          { image: `${IMG}/relais_prive-engine.webp`, motion: 'campaign' },
          { image: `${IMG}/relais_prive-guide.webp`, motion: 'parcours' },
          { image: `${IMG}/relais_prive-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Dalal ak diam ! Kii mooy Relais Privé. Khamal li mu di def.', keyword: 'Privé', durationMs: 4000 },
          { id: 'invited', text: 'Yaa di agent bénévole ou terrain bi, yaw la Parrain Grand Électeur woo, moom mi la wóolu pour nga dimbali bénéficiaire yi.', keyword: 'Parrain', durationMs: 7000 },
          { id: 'engine', text: 'Yaa di ki nekk ci kholu campagne bi, la massification. Yaa di ki di dem ci nit gni. yaa génn ngir daje ak askan wi.', keyword: 'massification', durationMs: 5000 },
          { id: 'guide', text: 'Application bi day la won li nga wara def, dale ko ci diagnostic ba ci parcours bu papiers yi — ngir yóbbu bénéficiaire bu nekk ba mu am statut « Inscrit »', keyword: 'Inscrit', durationMs: 9000 },
          { id: 'cta', text: 'Beugg nga dimbali sa gox ? Bokkal ci Grand-Électeur.', keyword: 'gox', durationMs: 5000, isCta: true },
        ],
      ),
      en: enLang(
        [
          { image: `${IMG}/relais_prive-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/relais_prive-invited.webp`, motion: 'field' },
          { image: `${IMG}/relais_prive-engine.webp`, motion: 'campaign' },
          { image: `${IMG}/relais_prive-guide.webp`, motion: 'parcours' },
          { image: `${IMG}/relais_prive-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Welcome! Discover the role of a Private Relay.', keyword: 'Private', durationMs: 4000 },
          { id: 'invited', text: 'You are a volunteer field agent, invited by a Grand Elector Sponsor who trusts you.', keyword: 'invited', durationMs: 6400 },
          { id: 'engine', text: 'You are the engine of their mass registration campaign—you go out to meet citizens.', keyword: 'engine', durationMs: 6800 },
          { id: 'guide', text: 'The app guides you step by step—from diagnosis to documents—until each beneficiary is registered.', keyword: 'registered', durationMs: 8200 },
          { id: 'cta', text: 'Ready to help register your community? Join Grand-Électeur.', keyword: 'community', durationMs: 4400, isCta: true },
        ],
      ),
    },
  },

  relais_public: {
    label: 'Relais Public',
    icon: '📣',
    registerRole: 'relais',
    languages: {
      fr: {
        langCode: 'fr-FR',
        audio: true,
        scenes: [
          {
            id: 'welcome',
            text: 'Bienvenue ! Découvrez le rôle du Relais Public.',
            keyword: 'Public',
            image: `${IMG}/relais_public-welcome.webp`,
            motion: 'welcome',
            durationMs: 4000,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'chosen',
            text: 'Vous êtes un agent de terrain bénévole, choisi et validé par les administrateurs de Grand-Électeur.',
            keyword: 'validé',
            image: `${IMG}/relais_public-chosen.webp`,
            motion: 'validate',
            durationMs: 6000,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'visible',
            text: 'Proche de votre communauté, vous êtes visible sur la plateforme : les bénéficiaires de votre localité peuvent vous contacter directement.',
            keyword: 'visible',
            image: `${IMG}/relais_public-visible.webp`,
            motion: 'network',
            durationMs: 7600,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'accompany',
            text: 'De votre plein gré, vous les accompagnez pas à pas, du diagnostic jusqu’au statut « Inscrit ».',
            keyword: 'accompagnez',
            image: `${IMG}/relais_public-accompany.webp`,
            motion: 'parcours',
            durationMs: 6000,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'cta',
            text: 'Prêt à servir votre communauté ? Rejoignez Grand-Électeur.',
            keyword: 'servir',
            image: `${IMG}/relais_public-cta.webp`,
            motion: 'cta',
            durationMs: 4200,
            isCta: true,
            audioSrc: null,
            timingsSrc: null,
          },
        ],
      },
      wo: woLang(
        [
          { image: `${IMG}/relais_public-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/relais_public-chosen.webp`, motion: 'validate' },
          { image: `${IMG}/relais_public-visible.webp`, motion: 'network' },
          { image: `${IMG}/relais_public-accompany.webp`, motion: 'parcours' },
          { image: `${IMG}/relais_public-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Dalal ak diam ! Kii mooy Relais Public. Khamal li mu di def.', keyword: 'Public', durationMs: 4000 },
          { id: 'chosen', text: 'Yaa di agent publique ci teren bi. Yaa di dimbali askan-wi. Ekip Grand-Électeur moo la tann té wolou la.', keyword: 'Grand-Électeur', durationMs: 6000 },
          { id: 'visible', text: 'Yaa djégué nit gni ci sa gox. Sa profile bi ci plateforme bi, dayy yomballal bénéficiaire yi gnou mène la gis, pour contacté la ngirr nga dimbeullilenn.', keyword: 'plateforme', durationMs: 6000 },
          { id: 'accompany', text: 'Ci sa coobare, dangay ànd ak ñoom ndànk-ndànk, dale ko ci diagnostic, ba ci nignou inscrirou si liste electoral bi', keyword: 'inscrirou', durationMs: 6000 },
          { id: 'cta', text: 'Beugg nga dimbali sa gox ? Boole ci Grand-Électeur.', keyword: 'gox', durationMs: 5000, isCta: true },
        ],
      ),
      en: enLang(
        [
          { image: `${IMG}/relais_public-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/relais_public-chosen.webp`, motion: 'validate' },
          { image: `${IMG}/relais_public-visible.webp`, motion: 'network' },
          { image: `${IMG}/relais_public-accompany.webp`, motion: 'parcours' },
          { image: `${IMG}/relais_public-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Welcome! Discover the role of a Public Relay.', keyword: 'Public', durationMs: 4000 },
          { id: 'chosen', text: 'You are a volunteer field agent, chosen and validated by Grand-Électeur administrators.', keyword: 'validated', durationMs: 6000 },
          { id: 'visible', text: 'Close to your community, you are visible on the platform: nearby beneficiaries can contact you directly.', keyword: 'visible', durationMs: 7600 },
          { id: 'accompany', text: 'Of your own accord, you accompany them step by step, from diagnosis until they are registered.', keyword: 'accompany', durationMs: 6000 },
          { id: 'cta', text: 'Ready to serve your community? Join Grand-Électeur.', keyword: 'serve', durationMs: 4200, isCta: true },
        ],
      ),
    },
  },

  beneficiaire: {
    label: 'Bénéficiaire',
    icon: '👤',
    registerRole: 'beneficiaire',
    languages: {
      fr: {
        langCode: 'fr-FR',
        audio: true,
        scenes: [
          {
            id: 'welcome',
            text: 'Bienvenue ! Voici comment Grand-Électeur vous aide.',
            keyword: 'Grand-Électeur',
            image: `${IMG}/beneficiaire-welcome.webp`,
            motion: 'welcome',
            durationMs: 4200,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'barriers',
            text: 'Vous voulez vous inscrire sur la liste électorale, mais des obstacles vous bloquent ?',
            keyword: 'obstacles',
            image: `${IMG}/beneficiaire-barriers.webp`,
            motion: 'barrier',
            durationMs: 5400,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'contact',
            text: 'Rejoignez la plateforme et contactez le Relais Public le plus proche de chez vous.',
            keyword: 'proche',
            image: `${IMG}/beneficiaire-contact.webp`,
            motion: 'network',
            durationMs: 5600,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'support',
            text: 'Il évalue rapidement votre situation, puis vous accompagne à chaque étape, jusqu’à votre inscription. Vous n’êtes plus seul.',
            keyword: 'accompagne',
            image: `${IMG}/beneficiaire-support.webp`,
            motion: 'accompaniment',
            durationMs: 7800,
            audioSrc: null,
            timingsSrc: null,
          },
          {
            id: 'cta',
            text: 'Prêt à devenir électeur ? Démarrez avec Grand-Électeur.',
            keyword: 'électeur',
            image: `${IMG}/beneficiaire-cta.webp`,
            motion: 'cta',
            durationMs: 4400,
            isCta: true,
            audioSrc: null,
            timingsSrc: null,
          },
        ],
      },
      wo: woLang(
        [
          { image: `${IMG}/beneficiaire-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/beneficiaire-barriers.webp`, motion: 'barrier' },
          { image: `${IMG}/beneficiaire-contact.webp`, motion: 'network' },
          { image: `${IMG}/beneficiaire-support.webp`, motion: 'accompaniment' },
          { image: `${IMG}/beneficiaire-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Dalal ak diam yawmi sokhla dimbeul ! Nii la Grand-Électeur di la dimbali.', keyword: 'Grand-Électeur', durationMs: 4000 },
          { id: 'barriers', text: 'Beugg nga bind sa tour ci liste électorale bi, waayé am nga ay djafé-djafé?', keyword: 'djafé-djafé', durationMs: 5000 },
          { id: 'contact', text: 'Bokkal ci plateforme Grand-Electeur te contacté Relais Public bi la gueuna djégué.', keyword: 'Relais', durationMs: 6000 },
          { id: 'support', text: 'Day sétt sa mbir bu gaaw, ba noppi mu ànd ak yaw ci etape bu nekk, ba nga inscrirou si liste electoral bi.', keyword: 'inscrirou', durationMs: 7500 },
          { id: 'cta', text: 'Beugg nga inscrirou ? Tambalill tey ak Grand-Électeur.', keyword: 'inscrirou', durationMs: 5000, isCta: true },
        ],
      ),
      en: enLang(
        [
          { image: `${IMG}/beneficiaire-welcome.webp`, motion: 'welcome' },
          { image: `${IMG}/beneficiaire-barriers.webp`, motion: 'barrier' },
          { image: `${IMG}/beneficiaire-contact.webp`, motion: 'network' },
          { image: `${IMG}/beneficiaire-support.webp`, motion: 'accompaniment' },
          { image: `${IMG}/beneficiaire-cta.webp`, motion: 'cta', isCta: true },
        ],
        [
          { id: 'welcome', text: 'Welcome! Here is how Grand-Électeur helps you.', keyword: 'Grand-Électeur', durationMs: 4200 },
          { id: 'barriers', text: 'You want to register on the electoral roll, but obstacles stand in your way?', keyword: 'obstacles', durationMs: 5400 },
          { id: 'contact', text: 'Join the platform and contact the Public Relay nearest to you.', keyword: 'nearest', durationMs: 5600 },
          { id: 'support', text: 'They assess your situation quickly, then support you at every step until you are registered. You are not alone.', keyword: 'support', durationMs: 7800 },
          { id: 'cta', text: 'Ready to become a voter? Get started with Grand-Électeur.', keyword: 'voter', durationMs: 4400, isCta: true },
        ],
      ),
    },
  },
};

/**
 * Récupère le contenu d'un rôle pour une langue, avec repli sur le français.
 * @param {string} roleKey - 'parrain_grand' | 'relais_prive' | 'relais_public' | 'beneficiaire'
 * @param {string} [lang='fr'] - langue demandée
 * @returns {{ roleKey, label, icon, registerRole, lang, langCode, scenes }|null}
 */
/**
 * Une langue est disponible pour un rôle si son bloc de contenu existe (non nul).
 * @param {string} roleKey
 * @param {string} lang
 * @returns {boolean}
 */
export function isLanguageAvailable(roleKey, lang) {
  return !!CONTENT[roleKey]?.languages?.[lang];
}

export function getExplainerModalUi(lang) {
  return EXPLAINER_MODAL_UI[lang] || EXPLAINER_MODAL_UI.fr;
}

export function getRoleContent(roleKey, lang = DEFAULT_EXPLAINER_LANG) {
  const role = CONTENT[roleKey];
  if (!role) return null;
  const effLang = role.languages[lang] ? lang : 'fr';
  const langData = role.languages[effLang];
  const hasAudio = !!langData.audio;
  const useNativeWo = effLang === 'wo' && !!langData.nativeAudio;

  let scenes = langData.scenes;
  if (useNativeWo) {
    scenes = applyNativeWoTranscripts(roleKey, langData.scenes);
    scenes = applyNativeWoAudio(roleKey, scenes);
  } else {
    // Convention ElevenLabs : /onboarding/audio/<lang>/<roleKey>-<sceneId>.mp3
    scenes = langData.scenes.map((s) => ({
      ...s,
      audioSrc:
        s.audioSrc
        || (hasAudio ? `/onboarding/audio/${effLang}/${roleKey}-${s.id}.mp3` : null),
      timingsSrc:
        s.timingsSrc
        || (hasAudio ? `/onboarding/audio/${effLang}/${roleKey}-${s.id}.timings.json` : null),
    }));
  }

  return {
    roleKey,
    label: ROLE_LABELS[roleKey]?.[effLang] || role.label,
    icon: role.icon,
    registerRole: role.registerRole,
    lang: effLang,
    langCode: langData.langCode,
    scenes,
  };
}

export default CONTENT;
